import "./index.css";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { UsersRepository } from "@/api/users/repository";
import { UsersService } from "@/api/users/service";
import { InputPasswordControl } from "@/components/controls";
import { Button, Card, Toast } from "@/ui-kit";
import { serverErrorHandler } from "@/utils/serverErrorHandler";

type IFormData = {
  old_password: string;
  new_password: string;
  repeat_password: string;
};

export const PasswordSettings = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  const repository = useMemo(
    () => new UsersRepository(new UsersService(), { abort: true }),
    [],
  );

  const schema = useMemo(() => {
    return z
      .object({
        old_password: z.string().min(1, { message: t("validation.required") }),
        new_password: z
          .string()
          .min(1, { message: t("validation.required") })
          .min(8, { message: t("validation.password_length") }),
        repeat_password: z
          .string()
          .min(1, { message: t("validation.required") })
          .min(8, { message: t("validation.password_length") }),
      })
      .refine((data) => data.new_password === data.repeat_password, {
        message: t("validation.passwords_must_match"),
        path: ["repeat_password"],
      });
  }, [t]);

  const form = useForm<IFormData>({
    defaultValues: {
      old_password: "",
      new_password: "",
      repeat_password: "",
    },
    resolver: zodResolver(schema),
  });

  const { handleSubmit, reset } = form;

  const onSubmit = useCallback(
    (formData: IFormData) => {
      setLoading(true);
      repository
        .updatePassword({
          oldPassword: formData.old_password,
          newPassword: formData.new_password,
        })
        .then(() => {
          Toast.success({ title: t("toast.password_updated_success") });
          reset();
        })
        .catch(
          serverErrorHandler(() => {
            setLoading(false);
          }),
        )
        .finally(() => {
          setLoading(false);
        });
    },
    [repository, reset, t],
  );

  return (
    <Card>
      <Card.Header title={t("action.changePassword")} />
      <Card.Body>
        <FormProvider {...form}>
          <form
            className="password-settings-form"
            noValidate
            onSubmit={handleSubmit(onSubmit)}
          >
            <InputPasswordControl
              label={t("label.old_password")}
              name="old_password"
              autoComplete="off"
            />

            <InputPasswordControl
              label={t("label.new_password")}
              name="new_password"
              autoComplete="off"
            />

            <InputPasswordControl
              label={t("label.repeat_password")}
              name="repeat_password"
              autoComplete="off"
            />

            <div className="password-settings-form__actions">
              <Button
                type="submit"
                content={t("action.changePassword")}
                loading={loading}
              />
            </div>
          </form>
        </FormProvider>
      </Card.Body>
    </Card>
  );
};
